import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../context/AuthContext';
import { SEO } from '../components/SEO';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Package, Search, Loader2, ExternalLink, Star, Clock, ShoppingBag } from 'lucide-react';

const CATEGORIES = [
  { id: 'all', name: 'Wszystkie' },
  { id: 'elektronika', name: 'Elektronika' },
  { id: 'moda', name: 'Moda' },
  { id: 'dom', name: 'Dom' },
  { id: 'sport', name: 'Sport' },
  { id: 'jedzenie', name: 'Jedzenie' },
  { id: 'rozrywka', name: 'Rozrywka' },
  { id: 'inne', name: 'Inne' },
];

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [category, setCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => { fetchProducts(); }, [page, category, search]);

  const fetchProducts = async () => {
    setLoading(true);
    const params = { page, limit: 16 };
    if (category !== 'all') params.category = category;
    if (search) params.search = search;
    try {
      const res = await axios.get(`${API}/products`, { params });
      setProducts(res.data.products);
      setTotalPages(res.data.pages);
    } catch {}
    setLoading(false);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 pb-20 md:pb-6" data-testid="products-page">
      <SEO title="Produkty" description="Baza produktów z profesjonalnymi opisami SEO. Najlepsze okazje z recenzjami ekspertów." />

      <div className="mb-6">
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-white flex items-center gap-2">
          <Package className="w-6 h-6 text-primary" /> BAZA PRODUKTÓW
        </h1>
        <p className="text-sm text-muted-foreground">Produkty z profesjonalnymi opisami SEO i recenzjami ekspertów</p>
      </div>

      {/* Search + Categories */}
      <form onSubmit={e => { e.preventDefault(); setSearch(searchInput); setPage(1); }} className="flex gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={searchInput} onChange={e => setSearchInput(e.target.value)} placeholder="Szukaj produktu..." className="pl-10 bg-input border-transparent focus:border-primary rounded-none" data-testid="products-search" />
        </div>
        <Button type="submit" className="bg-primary text-primary-foreground rounded-none px-4"><Search className="w-4 h-4" /></Button>
      </form>

      <div className="flex gap-2 overflow-x-auto pb-3 mb-6 scrollbar-hide">
        {CATEGORIES.map(c => (
          <button key={c.id} onClick={() => { setCategory(c.id); setPage(1); }}
            className={`flex-shrink-0 px-4 py-1.5 text-xs font-heading uppercase tracking-wider border transition-colors ${
              category === c.id ? 'border-primary text-primary bg-primary/10' : 'border-border/40 text-muted-foreground hover:border-primary/30'
            }`} data-testid={`prod-cat-${c.id}`}>{c.name}</button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>
      ) : products.length === 0 ? (
        <div className="text-center py-12">
          <Package className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-muted-foreground">Brak produktów z opisami SEO. Admin może wygenerować opisy w panelu.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="products-grid">
            {products.map(p => {
              const img = p.image_local_path ? `${BACKEND}${p.image_local_path}` : p.image_url;
              const buyUrl = p.affiliate_url || p.url;
              return (
                <div key={p.id} className="bg-card border border-border/40 hover:border-primary/30 transition-colors group overflow-hidden" data-testid={`product-card-${p.id}`}>
                  <Link to={`/produkt/${p.id}`}>
                    <div className="h-40 bg-secondary overflow-hidden relative">
                      {img ? (
                        <img src={img} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center"><Package className="w-8 h-8 text-muted-foreground/20" /></div>
                      )}
                      {p.deal_price && (
                        <div className="absolute bottom-2 left-2 price-glow px-2.5 py-1">
                          <span className="price-tag text-xl">{p.deal_price} zł</span>
                        </div>
                      )}
                    </div>
                  </Link>
                  <div className="p-3">
                    <Link to={`/produkt/${p.id}`}>
                      <h3 className="text-sm font-heading font-bold text-white group-hover:text-primary transition-colors line-clamp-2 leading-tight">{p.title}</h3>
                    </Link>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs text-primary/80">{p.category}</span>
                      {p.original_price && p.deal_price && p.original_price > p.deal_price && (
                        <span className="text-xs text-muted-foreground line-through">{p.original_price} zł</span>
                      )}
                    </div>
                    {buyUrl && (
                      <a href={buyUrl} target="_blank" rel="noopener noreferrer"
                        className="buy-now-btn mt-2 w-full inline-flex items-center justify-center gap-1.5 font-heading font-bold uppercase tracking-wider text-xs"
                        onClick={e => e.stopPropagation()} data-testid={`buy-product-${p.id}`}>
                        <ShoppingBag className="w-3.5 h-3.5" /> KUP TERAZ
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-6">
              <Button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="bg-secondary text-secondary-foreground rounded-none">Poprzednie</Button>
              <span className="flex items-center text-sm text-muted-foreground px-3">{page} / {totalPages}</span>
              <Button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="bg-secondary text-secondary-foreground rounded-none">Następne</Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
